import { useEffect, useRef, useState } from 'react';

const MAX_ZOOM = 6;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const INITIAL_VIEW = { scale: 1, x: 0, y: 0 };

const constrainView = (view, element) => {
  if (view.scale <= 1 || !element) return { scale: 1, x: 0, y: 0 };
  const maxX = element.clientWidth * (view.scale - 1) / 2;
  const maxY = element.clientHeight * (view.scale - 1) / 2;
  return { scale: view.scale, x: clamp(view.x, -maxX, maxX), y: clamp(view.y, -maxY, maxY) };
};

export const ZoomableImageViewport = ({ children, resetKey }) => {
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const [view, setView] = useState(INITIAL_VIEW);

  useEffect(() => {
    dragRef.current = null;
    setView(INITIAL_VIEW);
  }, [resetKey]);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;

    const handleWheel = (event) => {
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const pointX = event.clientX - rect.left - rect.width / 2;
      const pointY = event.clientY - rect.top - rect.height / 2;

      setView((current) => {
        const scale = clamp(current.scale * Math.exp(-event.deltaY * 0.002), 1, MAX_ZOOM);
        if (scale === current.scale) return current;
        const ratio = scale / current.scale;
        return constrainView({
          scale,
          x: current.x + (pointX - current.x) * (1 - ratio),
          y: current.y + (pointY - current.y) * (1 - ratio),
        }, element);
      });
    };

    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }, []);

  const changeZoom = (factor) => {
    setView((current) => constrainView({ ...current, scale: clamp(current.scale * factor, 1, MAX_ZOOM) }, viewportRef.current));
  };

  const handlePointerDown = (event) => {
    if (event.button !== 0 || view.scale <= 1 || event.target.closest('button')) return;
    dragRef.current = { id: event.pointerId, clientX: event.clientX, clientY: event.clientY, x: view.x, y: view.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    setView((current) => constrainView({
      ...current,
      x: drag.x + event.clientX - drag.clientX,
      y: drag.y + event.clientY - drag.clientY,
    }, viewportRef.current));
  };

  const handlePointerEnd = (event) => {
    if (dragRef.current?.id !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <div ref={viewportRef} className="absolute inset-0 overflow-hidden select-none" style={{ touchAction: view.scale > 1 ? 'none' : 'auto', cursor: view.scale > 1 ? 'grab' : 'default' }} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerEnd} onPointerCancel={handlePointerEnd}>
      <div className="absolute inset-0" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, transformOrigin: 'center center' }}>
        {children}
      </div>
      <div className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/60 px-2 py-1 text-[10px] text-white/75">
        Rueda: zoom · Clic y arrastrar para mover
      </div>
      <div className="absolute right-2 top-2 flex items-center gap-1 rounded bg-black/60 p-1">
        <button type="button" aria-label="Alejar imagen" disabled={view.scale <= 1} onClick={() => changeZoom(1 / 1.5)} className="flex h-6 w-6 items-center justify-center rounded text-sm text-white hover:bg-white/20 disabled:opacity-40">−</button>
        <button type="button" aria-label="Restablecer zoom" onClick={() => setView(INITIAL_VIEW)} className="min-w-10 rounded px-1 text-[11px] text-white hover:bg-white/20">{Math.round(view.scale * 100)}%</button>
        <button type="button" aria-label="Acercar imagen" disabled={view.scale >= MAX_ZOOM} onClick={() => changeZoom(1.5)} className="flex h-6 w-6 items-center justify-center rounded text-sm text-white hover:bg-white/20 disabled:opacity-40">+</button>
      </div>
    </div>
  );
};
